import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Typography
} from "@mui/material";
import ShowComponents from "../Common/ShowComponent";
import RestAPIService from "../../../../Utility/Services/RestAPIService";
import { Colors } from "../../../../Styles/Colors";
import { renderTitle } from "../Common/DocCommonComponent";
import { Serviceurls } from "../../../../Utility/API/Serviceurls";

/* ================= CARE PLAN LIST ================= */
const CARE_PLAN_OPTIONS = [
  "Medicines",
  "Diet Prescription",
  "Occupational Therapy",
  "Discharge",
  "Allopathic specialist advice",
  "Therapy",
  "Physiotherapy",
  "Nursing instructions",
  "Outcome measure",
  "Refer to another hospital",
  "Speech therapy",
  "Special Nursing",
  "Investigations",
  "Assistive devices",
  "Yoga",
  "Video Documentation"
];

const DocCarePlan = ({
  Title,
  appointmentId,
  ipAdmissionId,
  clinicId,
  doctorId,
  isFromIp,
  patientId,
  isSelected,
  successMessage,
  errorMessage
}) => {

  const [state, setState] = useState({
    selectedPlans: [],
    isLoading: false
  });

  const updateState = (obj) =>
    setState((prev) => ({ ...prev, ...obj }));

  /* ================= GET ================= */
  const getCarePlan = useCallback(() => {
    const params = isFromIp ? `?ip_admission_id=${ipAdmissionId}&clinic_id=${clinicId}&doctor_id=${doctorId}` : `?appointment_id=${appointmentId}`;
    RestAPIService.getAll(Serviceurls.DOC_CARE_PLAN + params)
      .then((res) => {
        if (
          res?.data?.status === "success" &&
          Array.isArray(res?.data?.data?.care_plan)
        ) {
          updateState({
            selectedPlans: res.data.data.care_plan
          });
        } else {
          updateState({ selectedPlans: [] });
        }
      })
      .catch((err) => errorMessage(err?.message));
  }, [appointmentId, isFromIp, ipAdmissionId]);

  /* ================= TOGGLE ================= */
  const togglePlan = (value) => {
    const exists = state.selectedPlans.includes(value);

    updateState({
      selectedPlans: exists
        ? state.selectedPlans.filter((v) => v !== value)
        : [...state.selectedPlans, value]
    });
  };

  /* ================= SAVE ================= */
  const saveCarePlan = () => {
    if (!state.selectedPlans.length)
      return errorMessage("Please select at least one care plan");

    const payload = {
      patient_id: patientId,
      care_plan: state.selectedPlans
    };
    if (isFromIp) {
      payload.ip_admission_id = ipAdmissionId;
      payload.clinic_id = clinicId;
      payload.doctor_id = doctorId;
    } else {
      payload.appointment_id = appointmentId;
    }

    updateState({ isLoading: true });

    RestAPIService.create(payload, Serviceurls.DOC_CARE_PLAN)
      .then((res) => {
        if (res.data.status === "success") {
          successMessage(res.data.message || "Care Plan saved");
          getCarePlan();
        } else {
          errorMessage(res.data.message);
        }
      })
      .catch((err) =>
        errorMessage(err?.response?.data?.message || err.message)
      )
      .finally(() => updateState({ isLoading: false }));
  };

  /* ================= CLEAR ================= */
  const clearCarePlan = () => {
    updateState({ selectedPlans: [] });
  };

  useEffect(() => {
    getCarePlan();
  }, [getCarePlan]);

  /* ================= UI ================= */
  return (
    <ShowComponents onIntersection={getCarePlan}>
      <Card
        sx={{
          width: "100%",
          border: `2px solid ${
            isSelected ? Colors.themeDark : Colors.white
          }`,
          padding: "0.5vw"
        }}
      >
        {/* HEADER */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid #ddd"
          p="0.5vw"
        >
          {renderTitle(Title || "Care Plan", isSelected)}

          <Box display="flex" gap="0.6vw">
            <Button
              variant="outlined"
              onClick={clearCarePlan}
              disabled={state.isLoading}
            >
              CLEAR
            </Button>

            <Button
              variant="contained"
              onClick={saveCarePlan}
              disabled={state.isLoading}
            >
              SAVE
            </Button>
          </Box>
        </Box>

        {/* CHECKBOX GRID */}
        <Box
          display="grid"
          gridTemplateColumns="repeat(4, 1fr)"
          gap="0.8vw"
          mt={1}
        >
          {CARE_PLAN_OPTIONS.map((item) => {
            const checked = state.selectedPlans.includes(item);

            return (
              <Box
                key={item}
                display="flex"
                alignItems="center"
                sx={{
                  padding: "0.3vw",
                  backgroundColor: checked ? "#e3f2fd" : "transparent",
                  borderRadius: "4px"
                }}
              >
                <Checkbox
                  checked={checked}
                  onChange={() => togglePlan(item)}
                />
                <Typography fontSize="0.85vw">
                  {item}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Card>
    </ShowComponents>
  );
};

export default DocCarePlan;
